import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAnalytics } from '../hooks/useAnalytics';
interface FloatingCTAProps {
  ctaText?: string;
  ctaLink: string;
  subText?: string;
}
export function FloatingCTA({
  ctaText = 'Try GINEXT Risk-Free',
  ctaLink,
  subText = '30-Day Money Back Guarantee'
}: FloatingCTAProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [bundlesVisible, setBundlesVisible] = useState(false);
  const {
    trackClick
  } = useAnalytics();
  // Track scroll percentage
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
          const scrollPercentage = scrollTop / scrollHeight * 100;
          // Show after 20% scroll AND bundles section not visible
          setIsVisible(scrollPercentage > 20 && !bundlesVisible);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [bundlesVisible]);
  // Track bundles section visibility
  useEffect(() => {
    const bundlesSection = document.getElementById('bundles');
    if (!bundlesSection) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        setBundlesVisible(entry.isIntersecting);
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px' // Hide CTA slightly before reaching section
    });
    observer.observe(bundlesSection);
    return () => observer.disconnect();
  }, []);
  return <AnimatePresence data-id="element-979">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: 100
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 100
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none" data-id="element-980">
          <div className="max-w-lg mx-auto px-4 pb-4 sm:pb-6 pointer-events-auto" data-id="element-981">
            <div className="flex flex-col items-center" data-id="element-982">
              {/* Yellow Badge */}
              <span className="relative z-10 mb-[-12px] inline-block bg-[#F5D24B] text-[#1A1A1A] text-[10px] sm:text-xs font-extrabold uppercase tracking-wider px-3.5 py-1 rounded-full shadow-sm" data-id="element-983">
                10,000+ Bottles Sold
              </span>

              {/* Green CTA Button */}
              <a href={ctaLink} className="block w-full scroll-smooth" onClick={() => trackClick({
            location: 'floating_cta',
            destination: ctaLink
          })} data-id="element-984">
                <motion.div whileHover={{
              scale: 1.02,
              boxShadow: '0 12px 32px -8px rgba(34, 120, 60, 0.4)'
            }} whileTap={{
              scale: 0.98
            }} className="w-full bg-[#1B6B3A] text-white px-8 py-3.5 sm:py-4 rounded-full font-black text-base sm:text-lg shadow-xl transition-all duration-300 text-center tracking-tight" data-id="element-985">
                  Save 60% + Free Shipping
                </motion.div>
              </a>

              {/* Risk-Free Line */}
              <div className="mt-2 flex items-center justify-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-1.5 rounded-full shadow-sm" data-id="element-986">
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-987">
                  <circle cx="10" cy="10" r="10" fill="#4CAF50" data-id="element-988" />
                  <path d="M6 10.5l2.5 2.5L14 8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-989" />
                </svg>
                <span className="text-xs sm:text-sm text-charcoal font-medium" data-id="element-990">
                  Try It{' '}
                  <span className="underline underline-offset-2 decoration-charcoal" data-id="element-991">
                    Risk-Free For 30 Days
                  </span>
                </span>
              </div>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}